// @flow

import {
	TimePickerComponent,
	useTimePickerComponent,
} from "@/components/range-picker/time-picker.component";
import { TimeService, getMomentTz } from "@dexteel/mesf-core";
import { Grid, Paper } from "@material-ui/core";
import { DatePicker, StaticDatePicker, TimeField } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";

type Props = {
	onChange: (date: Date) => void;
	value: Date | null;
	minDate?: Date;
	maxDate?: Date;
};

const moment = getMomentTz();

const fieldPadding = { "& .MuiOutlinedInput-input": { paddingLeft: "12px" } };

const compactCalendar = {
	"& .MuiDateCalendar-root": { height: "auto", maxHeight: "none" },
	"& .MuiPickersCalendarHeader-root": { marginTop: 4, marginBottom: 0 },
	"& .MuiPickersToolbar-root": {
		padding: "4px 8px",
		justifyContent: "center",
	},
	"& .MuiDatePickerToolbar-title": {
		fontSize: 28,
		lineHeight: 1.2,
		whiteSpace: "pre-line",
	},
	"& .MuiPickersLayout-actionBar": { padding: 0 },
	"& .MuiPickersLayout-actionBar .MuiButton-root": {
		fontSize: 12,
		minWidth: 80,
		padding: "2px 8px",
		whiteSpace: "nowrap",
	},
};

export const DateTimePickerComponent = ({
	onChange,
	value = new Date(),
	minDate,
	maxDate,
}: Props) => {
	const { state: timePickerState, actions: timePickerActions } =
		useTimePickerComponent(value);

	const [date, setStartDate] = useState<Date>(value || new Date());
	const newMinutes = timePickerState.minutes;

	useEffect(() => {
		const newValue = moment(date)
			.set({
				hours: timePickerState.hours,
				minutes: timePickerState.minutes,
				seconds: 0,
				milliseconds: 0,
			})
			.toDate();

		onChange(newValue);
	}, [date, timePickerState.hours, timePickerState.minutes]);

	return (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<Paper style={{ padding: 8 }}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6}>
							<DatePicker
								value={moment(value)}
								minDate={minDate ? moment(minDate) : undefined}
								maxDate={maxDate ? moment(maxDate) : undefined}
								onChange={(date, _) => {
									setStartDate(date?.toDate() || new Date());
								}}
								timezone={TimeService.getInstance().getServerTimeZone()}
								slotProps={{
									openPickerButton: { disabled: true },
									textField: {
										size: "small",
										fullWidth: true,
										sx: fieldPadding,
									},
								}}
								label="Date"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TimeField
								value={moment(date).set({
									hours: timePickerState.hours,
									minutes: newMinutes,
								})}
								readOnly
								format="HH:mm"
								label="Time"
								size="small"
								fullWidth
								sx={fieldPadding}
							/>
						</Grid>
					</Grid>
				</Paper>
			</Grid>

			<Grid item xs={12} container spacing={2}>
				<Grid
					item
					xs={12}
					md={7}
					container
					justifyContent="center"
					alignItems="center"
				>
					<StaticDatePicker
						value={moment(value)}
						minDate={minDate ? moment(minDate) : undefined}
						maxDate={maxDate ? moment(maxDate) : undefined}
						onChange={(date, _) => {
							setStartDate(date?.toDate() || new Date());
						}}
						timezone={TimeService.getInstance().getServerTimeZone()}
						orientation={"landscape"}
						sx={compactCalendar}
						slotProps={{
							actionBar: {
								actions: ["today"],
							},
							toolbar: {
								toolbarFormat: "ddd,[\n]MMM D",
							},
						}}
					/>
				</Grid>
				<Grid item xs={12} md={5}>
					<TimePickerComponent
						state={timePickerState}
						actions={timePickerActions}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
};
